<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { ManageOrganisationService } from '@/services/manageOrganisation.services';
import { ManageOrganisationMutation } from '@/mutations/manageOrganisation.mutations';
import axiosInstance from '@/services/interceptor.services';

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const id = computed(() => route.params.id as string);
const {
  handleUpdateOrganisation,
  handleDeactivateOrganisation,
  updateOrganisation,
  deactivateOrganisation,
} = ManageOrganisationMutation();

// ── Types inlinés calqués sur la vraie réponse API ─────────────────────────
interface OrgAddress {
  id: string;
  label: string | null;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  isPrimary: boolean;
}
interface OrgContact {
  id: string;
  firstName: string;
  lastName: string;
  position: string | null;
  isPrimary: boolean;
  email?: string;
  phone?: string;
}
interface OrgStore {
  id: string;
  designation: string;
  slug: string | null;
  isActive: boolean;
  logoUrl: string | null;
  activityId?: string | null;
  schedules?: { weekday: string; openTime: string; closeTime: string }[];
}
interface OrgDetail {
  id: string;
  name: string;
  slug: string | null;
  type: string | null;
  taxIdentificationNumber: string | null;
  isActive: boolean;
  description: string | null;
  logoUrl: string | null;
  countryCode?: string;
  countryName?: string;
  createdAt: string;
  updatedAt: string;
  storeCount?: number;
  addresses: OrgAddress[];
  contacts: OrgContact[];
  stores: OrgStore[];
}

// ── Un seul fetch : /full retourne l'objet complet avec addresses[] et contacts[] ──
const {
  data: org,
  isPending,
  isError,
} = useQuery({
  queryKey: ['org-detail-full', id],
  queryFn: async () => {
    const r = await ManageOrganisationService.getOrganisationContactAdress(
      id.value,
    );
    return r.data as unknown as OrgDetail;
  },
  enabled: computed(() => !!id.value),
});

// ── Stores via restaurants/search (données enrichies) ─────────────────────
const { data: storesRaw } = useQuery({
  queryKey: ['org-stores', id],
  queryFn: async () => {
    const r = await axiosInstance.get<{ data: OrgStore[] }>(
      `restaurants/search?pagination=true&page=1&pageSize=50&organizationId=${id.value}`,
    );
    return r.data.data ?? [];
  },
  enabled: computed(() => !!id.value),
});

// Utilise les stores enrichis si dispo, sinon ceux de l'org
const stores = computed<OrgStore[]>(() =>
  storesRaw.value?.length ? storesRaw.value : (org.value?.stores ?? []),
);

// ── Collections (catégories) de cette organisation ─────────────────────────
const colPage = ref(1);
const COL_PAGE_SIZES = [5, 10, 20, 50] as const;
const colPageSize = ref<(typeof COL_PAGE_SIZES)[number]>(5);
watch(colPageSize, () => {
  colPage.value = 1;
});

const { data: orgCollectionsResp } = useQuery({
  queryKey: computed(() => [
    'org-collections',
    id.value,
    colPage.value,
    colPageSize.value,
  ]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `products/categories/search?pagination=true&page=${colPage.value}&pageSize=${colPageSize.value}&organizationIds=${id.value}&sortBy=position&sortOrder=ASC`,
    );
    return r.data as any;
  },
  enabled: computed(() => !!id.value),
});
const orgCollections = computed<any[]>(
  () => orgCollectionsResp.value?.data ?? [],
);
const orgCollectionsTotal = computed(
  () => orgCollectionsResp.value?.total ?? 0,
);
const orgCollectionsTotalPages = computed(() =>
  Math.max(1, Math.ceil(orgCollectionsTotal.value / colPageSize.value)),
);
function setColPage(p: number) {
  colPage.value = Math.max(1, Math.min(p, orgCollectionsTotalPages.value));
}
function collectionDesignation(cat: any): string {
  return cat.designationAlternatives?.fr ?? cat.designation ?? '—';
}

const addresses = computed<OrgAddress[]>(() => org.value?.addresses ?? []);
const contacts = computed<OrgContact[]>(() => org.value?.contacts ?? []);
const activeStores = computed(() => stores.value.filter((s) => s.isActive));

// ── Filtres commerces ──────────────────────────────────────────────────────
const storeSearch = ref('');
const storeStatus = ref<'all' | 'active' | 'inactive'>('all');
const storeSort = ref<'name' | 'status'>('name');
const storeSortDir = ref<'asc' | 'desc'>('asc');

function toggleStoreSort(key: 'name' | 'status') {
  if (storeSort.value === key)
    storeSortDir.value = storeSortDir.value === 'asc' ? 'desc' : 'asc';
  else {
    storeSort.value = key;
    storeSortDir.value = 'asc';
  }
}

const STORE_STATUS_OPTS = computed<
  { k: 'all' | 'active' | 'inactive'; l: string }[]
>(() => [
  { k: 'all', l: t('clientDetail.storeStatusOpts.all') },
  { k: 'active', l: t('clientDetail.storeStatusOpts.active') },
  { k: 'inactive', l: t('clientDetail.storeStatusOpts.inactive') },
]);

const filteredStores = computed(() => {
  let list = stores.value;
  const q = storeSearch.value.trim().toLowerCase();
  if (q)
    list = list.filter(
      (s) =>
        s.designation.toLowerCase().includes(q) ||
        (s.slug ?? '').toLowerCase().includes(q),
    );
  if (storeStatus.value === 'active') list = list.filter((s) => s.isActive);
  if (storeStatus.value === 'inactive') list = list.filter((s) => !s.isActive);
  return [...list].sort((a, b) => {
    let va: any, vb: any;
    if (storeSort.value === 'name') {
      va = a.designation.toLowerCase();
      vb = b.designation.toLowerCase();
    }
    if (storeSort.value === 'status') {
      va = a.isActive ? 1 : 0;
      vb = b.isActive ? 1 : 0;
    }
    if (va < vb) return storeSortDir.value === 'asc' ? -1 : 1;
    if (va > vb) return storeSortDir.value === 'asc' ? 1 : -1;
    return 0;
  });
});

// ── Helpers ────────────────────────────────────────────────────────────────
const TYPE_LABELS = computed<Record<string, string>>(() => ({
  INDIVIDUAL: t('clientDetail.type.INDIVIDUAL'),
  COMPANY: t('clientDetail.type.COMPANY'),
  ASSOCIATION: t('clientDetail.type.ASSOCIATION'),
  PUBLIC_ENTITY: t('clientDetail.type.PUBLIC_ENTITY'),
}));
const POSITION_LABELS = computed<Record<string, string>>(() => ({
  legal_representative: t('clientDetail.position.legal_representative'),
  manager: t('clientDetail.position.manager'),
  ceo: t('clientDetail.position.ceo'),
  cfo: t('clientDetail.position.cfo'),
  cto: t('clientDetail.position.cto'),
  contact: t('clientDetail.position.contact'),
}));
const ACTIVITY_LABELS = computed<Record<string, string>>(() => ({
  HARDWARE_STORE: t('clientDetail.activity.HARDWARE_STORE'),
  ELECTRONICS_STORE: t('clientDetail.activity.ELECTRONICS_STORE'),
  BAKERY: t('clientDetail.activity.BAKERY'),
  RESTAURANT: t('clientDetail.activity.RESTAURANT'),
  CAFE: t('clientDetail.activity.CAFE'),
}));
const WEEKDAY = computed<Record<string, string>>(() => ({
  monday: t('clientDetail.weekday.monday'),
  tuesday: t('clientDetail.weekday.tuesday'),
  wednesday: t('clientDetail.weekday.wednesday'),
  thursday: t('clientDetail.weekday.thursday'),
  friday: t('clientDetail.weekday.friday'),
  saturday: t('clientDetail.weekday.saturday'),
  sunday: t('clientDetail.weekday.sunday'),
}));

function typeLabel(type: string | null) {
  return type ? (TYPE_LABELS.value[type] ?? type) : '—';
}
function posLabel(p: string | null) {
  return p ? (POSITION_LABELS.value[p] ?? p.replace(/_/g, ' ')) : '—';
}
function actLabel(a: string | null) {
  return a ? (ACTIVITY_LABELS.value[a] ?? a) : '—';
}
function initials(name: string) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
function fmtAddr(a: OrgAddress) {
  return (
    [a.addressLine1, a.postalCode, a.city, a.country]
      .filter(Boolean)
      .join(' · ') || '—'
  );
}
function fmtHour(t: string) {
  return t.slice(0, 5);
}

const AVATAR_COLORS = [
  ['#fff7ee', '#f68402'],
  ['#eef3ff', '#3b5bdb'],
  ['#f0fff4', '#2f9e44'],
  ['#fff0f6', '#c2255c'],
  ['#f8f0fe', '#7048e8'],
  ['#e6fcf5', '#0c8599'],
];
function avatarStyle(name: string) {
  const i = (name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length;
  return { background: AVATAR_COLORS[i][0], color: AVATAR_COLORS[i][1] };
}

// ── Modal édition ──────────────────────────────────────────────────────────
const editOpen = ref(false);
const editError = ref<string | null>(null);
const editForm = ref({
  name: '',
  type: '',
  taxIdentificationNumber: '',
  country: '',
  description: '',
});

const ORG_TYPES_EDIT = computed(() => [
  { value: 'COMPANY', label: t('clientDetail.type.COMPANY') },
  { value: 'INDIVIDUAL', label: t('clientDetail.type.INDIVIDUAL') },
  { value: 'ASSOCIATION', label: t('clientDetail.type.ASSOCIATION') },
  { value: 'PUBLIC_ENTITY', label: t('clientDetail.type.PUBLIC_ENTITY') },
]);

function openEdit() {
  if (!org.value) return;
  editForm.value = {
    name: org.value.name ?? '',
    type: org.value.type ?? 'COMPANY',
    taxIdentificationNumber: org.value.taxIdentificationNumber ?? '',
    country: org.value.countryCode ?? '',
    description: org.value.description ?? '',
  };
  editError.value = null;
  editOpen.value = true;
}

async function submitEdit() {
  editError.value = null;
  try {
    await handleUpdateOrganisation(id.value, {
      name: editForm.value.name || undefined,
      type: (editForm.value.type as any) || undefined,
      taxIdentificationNumber:
        editForm.value.taxIdentificationNumber || undefined,
      country: editForm.value.country || undefined,
      description: editForm.value.description || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ['org-detail-full', id] });
    await queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] });
    await queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] });
    editOpen.value = false;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    editError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur lors de la mise à jour');
  }
}

// ── Activation ────────────────────────────────────────────────────────────
async function submitActivate() {
  try {
    await handleUpdateOrganisation(id.value, { isActive: true });
    await queryClient.invalidateQueries({ queryKey: ['org-detail-full', id] });
    await queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] });
    await queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] });
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    console.error(Array.isArray(raw) ? raw[0] : (raw ?? err?.message));
  }
}

// ── Désactivation ──────────────────────────────────────────────────────────
const deactivateConfirm = ref(false);
const deactivateError = ref<string | null>(null);

async function submitDeactivate() {
  deactivateError.value = null;
  try {
    await handleDeactivateOrganisation(id.value);
    await queryClient.invalidateQueries({ queryKey: ['org-detail-full', id] });
    await queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] });
    await queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] });
    deactivateConfirm.value = false;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    deactivateError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur lors de la désactivation');
  }
}
</script>

<template>
  <div class="cdp__page">
    <!-- ── Skeleton ───────────────────────────────────────────────────────── -->
    <div v-if="isPending" class="cdp__skeletons">
      <div class="cdp__sk cdp__sk--header" />
      <div class="cdp__sk__row">
        <div class="cdp__sk cdp__sk--card" />
        <div class="cdp__sk cdp__sk--main" />
      </div>
    </div>

    <!-- ── Erreur ─────────────────────────────────────────────────────────── -->
    <div v-else-if="isError" class="cdp__error">
      <i class="pi pi-exclamation-circle cdp__error-icon" />
      <p>{{ t('clientDetail.loadError') }}</p>
      <button class="cdp__btn cdp__btn--ghost" @click="router.back()">
        <i class="pi pi-arrow-left" /> {{ t('clientDetail.back') }}
      </button>
    </div>

    <div v-else-if="org" class="cdp">
      <!-- ── Breadcrumb + Header ────────────────────────────────────────────── -->
      <header class="cdp__header">
        <nav class="cdp__breadcrumb">
          <button class="cdp__breadcrumb-link" @click="router.push('/clients')">
            {{ t('clientDetail.breadcrumb') }}
          </button>
          <i class="pi pi-angle-right cdp__breadcrumb-sep" />
          <span class="cdp__breadcrumb-cur">{{ org.name }}</span>
        </nav>
        <div class="cdp__title-row">
          <div class="cdp__title-left">
            <div
              class="cdp__avatar cdp__avatar--xl"
              :style="avatarStyle(org.name)"
            >
              <img v-if="org.logoUrl" :src="org.logoUrl" :alt="org.name" />
              <span v-else>{{ initials(org.name) }}</span>
            </div>
            <div class="cdp__title-info">
              <div class="cdp__title-name-row">
                <h1 class="cdp__title-name">{{ org.name }}</h1>
                <span
                  class="cdp__status"
                  :class="
                    org.isActive
                      ? 'cdp__status--active'
                      : 'cdp__status--inactive'
                  "
                >
                  <span class="cdp__status-dot" />{{
                    org.isActive
                      ? t('clientDetail.statusActive')
                      : t('clientDetail.statusInactive')
                  }}
                </span>
              </div>
              <p class="cdp__title-sub">
                <template v-if="org.taxIdentificationNumber">
                  <i class="pi pi-hashtag" /> {{ org.taxIdentificationNumber }}
                  <span class="cdp__title-sep">·</span>
                </template>
                <i class="pi pi-flag" />
                {{ org.countryName ?? org.countryCode ?? '—' }}
                <span class="cdp__title-sep">·</span>
                <i class="pi pi-tag" /> {{ typeLabel(org.type) }}
              </p>
            </div>
          </div>
          <div class="cdp__header-actions">
            <button class="cdp__btn cdp__btn--ghost" @click="router.back()">
              <i class="pi pi-arrow-left" /> {{ t('clientDetail.back') }}
            </button>
            <button
              class="cdp__btn cdp__btn--ghost"
              @click="router.push(`/clients/${id}/edit`)"
            >
              <i class="pi pi-pencil" /> {{ t('clientDetail.edit') }}
            </button>
            <button
              v-if="org.isActive"
              class="cdp__btn cdp__btn--danger"
              @click="deactivateConfirm = true"
            >
              <i class="pi pi-ban" /> {{ t('clientDetail.deactivate') }}
            </button>
            <button
              v-else
              class="cdp__btn cdp__btn--success"
              @click="submitActivate"
            >
              <span
                v-if="updateOrganisation.isPending.value"
                class="cdp__spinner"
              />
              <i v-else class="pi pi-check-circle" />
              {{ t('clientDetail.activate') }}
            </button>
            <button
              class="cdp__btn cdp__btn--primary"
              @click="router.push('/commerces/new')"
            >
              <i class="pi pi-plus" /> {{ t('clientDetail.newStore') }}
            </button>
          </div>
        </div>
      </header>

      <!-- ── Alerte inactive ───────────────────────────────────────────────── -->
      <div v-if="!org.isActive" class="cdp__alert cdp__alert--warning">
        <i class="pi pi-exclamation-triangle cdp__alert-icon" />
        <div>
          <strong>{{ t('clientDetail.alert.disabledTitle') }}</strong> —
          {{ t('clientDetail.alert.disabledBody') }}
        </div>
      </div>

      <!-- ── KPIs ──────────────────────────────────────────────────────────── -->
      <div class="cdp__kpis">
        <div class="cdp__kpi">
          <div class="cdp__kpi-icon"><i class="pi pi-shop" /></div>
          <div class="cdp__kpi-body">
            <span class="cdp__kpi-label">{{
              t('clientDetail.kpi.stores')
            }}</span>
            <span class="cdp__kpi-val">{{ stores.length }}</span>
            <span class="cdp__kpi-sub">{{
              t('clientDetail.kpi.storesActive', activeStores.length)
            }}</span>
          </div>
        </div>
        <div class="cdp__kpi">
          <div class="cdp__kpi-icon"><i class="pi pi-map-marker" /></div>
          <div class="cdp__kpi-body">
            <span class="cdp__kpi-label">{{
              t('clientDetail.kpi.addresses')
            }}</span>
            <span class="cdp__kpi-val">{{ addresses.length }}</span>
            <span class="cdp__kpi-sub">{{
              addresses.find((a) => a.isPrimary)?.city ??
              addresses[0]?.city ??
              '—'
            }}</span>
          </div>
        </div>
        <div class="cdp__kpi">
          <div class="cdp__kpi-icon"><i class="pi pi-user" /></div>
          <div class="cdp__kpi-body">
            <span class="cdp__kpi-label">{{
              t('clientDetail.kpi.contacts')
            }}</span>
            <span class="cdp__kpi-val">{{ contacts.length }}</span>
            <span class="cdp__kpi-sub">
              {{
                (contacts.find((c) => c.isPrimary) ?? contacts[0])
                  ? `${(contacts.find((c) => c.isPrimary) ?? contacts[0]).firstName} ${(contacts.find((c) => c.isPrimary) ?? contacts[0]).lastName}`
                  : '—'
              }}
            </span>
          </div>
        </div>
        <div class="cdp__kpi">
          <div class="cdp__kpi-icon cdp__kpi-icon--muted">
            <i class="pi pi-receipt" />
          </div>
          <div class="cdp__kpi-body">
            <span class="cdp__kpi-label">{{ t('clientDetail.kpi.tax') }}</span>
            <span class="cdp__kpi-val cdp__kpi-val--sm">{{
              org.taxIdentificationNumber || '—'
            }}</span>
            <span class="cdp__kpi-sub">{{ typeLabel(org.type) }}</span>
          </div>
        </div>
      </div>

      <!-- ── Corps ─────────────────────────────────────────────────────────── -->
      <div class="cdp__body">
        <!-- Colonne gauche ──────────────────────────────────────────────────── -->
        <div class="cdp__col cdp__col--left">
          <!-- Fiche société -->
          <section class="cdp__card">
            <div class="cdp__card-header">
              <span class="cdp__card-title"
                ><i class="pi pi-building" />
                {{ t('clientDetail.section.orgCard') }}</span
              >
            </div>
            <div class="cdp__fields">
              <div class="cdp__field">
                <span class="cdp__field-lbl">{{
                  t('clientDetail.field.name')
                }}</span>
                <span class="cdp__field-val">{{ org.name }}</span>
              </div>
              <div class="cdp__field">
                <span class="cdp__field-lbl">{{
                  t('clientDetail.field.tax')
                }}</span>
                <span class="cdp__field-val cdp__field-val--mono">{{
                  org.taxIdentificationNumber || '—'
                }}</span>
              </div>
              <div class="cdp__field">
                <span class="cdp__field-lbl">{{
                  t('clientDetail.field.type')
                }}</span>
                <span class="cdp__field-val"
                  ><span class="cdp__type-chip">{{
                    typeLabel(org.type)
                  }}</span></span
                >
              </div>
              <div class="cdp__field">
                <span class="cdp__field-lbl">{{
                  t('clientDetail.field.country')
                }}</span>
                <span class="cdp__field-val">{{
                  org.countryName ?? org.countryCode ?? '—'
                }}</span>
              </div>
              <div v-if="org.slug" class="cdp__field">
                <span class="cdp__field-lbl">{{
                  t('clientDetail.field.slug')
                }}</span>
                <span class="cdp__field-val cdp__field-val--mono">{{
                  org.slug
                }}</span>
              </div>
              <div v-if="org.description" class="cdp__field">
                <span class="cdp__field-lbl">{{
                  t('clientDetail.field.description')
                }}</span>
                <span class="cdp__field-val">{{ org.description }}</span>
              </div>
            </div>
          </section>

          <!-- Adresses -->
          <section class="cdp__card">
            <div class="cdp__card-header">
              <span class="cdp__card-title"
                ><i class="pi pi-map-marker" />
                {{ t('clientDetail.section.addresses') }}</span
              >
              <span class="cdp__card-count">{{ addresses.length }}</span>
            </div>
            <div v-if="!addresses.length" class="cdp__empty-inline">
              {{ t('clientDetail.empty.addresses') }}
            </div>
            <div v-else class="cdp__list">
              <div
                v-for="addr in addresses"
                :key="addr.id"
                class="cdp__list-item"
                :class="{ 'cdp__list-item--primary': addr.isPrimary }"
              >
                <div class="cdp__list-item-icon">
                  <i class="pi pi-map-marker" />
                </div>
                <div class="cdp__list-item-body">
                  <div class="cdp__list-item-head">
                    <span class="cdp__list-item-name">{{
                      addr.label ?? 'Adresse'
                    }}</span>
                    <span v-if="addr.isPrimary" class="cdp__badge-primary">{{
                      t('clientDetail.primary')
                    }}</span>
                  </div>
                  <p class="cdp__list-item-detail">{{ fmtAddr(addr) }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Contacts -->
          <section class="cdp__card">
            <div class="cdp__card-header">
              <span class="cdp__card-title"
                ><i class="pi pi-user" />
                {{ t('clientDetail.section.contacts') }}</span
              >
              <span class="cdp__card-count">{{ contacts.length }}</span>
            </div>
            <div v-if="!contacts.length" class="cdp__empty-inline">
              {{ t('clientDetail.empty.contacts') }}
            </div>
            <div v-else class="cdp__list">
              <div
                v-for="c in contacts"
                :key="c.id"
                class="cdp__list-item"
                :class="{ 'cdp__list-item--primary': c.isPrimary }"
              >
                <div
                  class="cdp__contact-avatar"
                  :style="avatarStyle(c.firstName)"
                >
                  {{ (c.firstName?.[0] ?? '') + (c.lastName?.[0] ?? '') }}
                </div>
                <div class="cdp__list-item-body">
                  <div class="cdp__list-item-head">
                    <span class="cdp__list-item-name"
                      >{{ c.firstName }} {{ c.lastName }}</span
                    >
                    <span v-if="c.isPrimary" class="cdp__badge-primary">{{
                      t('clientDetail.primary')
                    }}</span>
                  </div>
                  <p class="cdp__list-item-pos">{{ posLabel(c.position) }}</p>
                  <div class="cdp__contact-links">
                    <a
                      v-if="c.email"
                      :href="`mailto:${c.email}`"
                      class="cdp__contact-link"
                    >
                      <i class="pi pi-envelope" /> {{ c.email }}
                    </a>
                    <a
                      v-if="c.phone"
                      :href="`tel:${c.phone}`"
                      class="cdp__contact-link"
                    >
                      <i class="pi pi-phone" /> {{ c.phone }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Colonne droite ──────────────────────────────────────────────────── -->
        <div class="cdp__col cdp__col--right">
          <!-- Commerces rattachés -->
          <section class="cdp__card">
            <div class="cdp__card-header">
              <span class="cdp__card-title"
                ><i class="pi pi-shop" />
                {{ t('clientDetail.section.stores') }}</span
              >
              <span class="cdp__card-count"
                >{{ filteredStores.length
                }}<template v-if="filteredStores.length !== stores.length">
                  / {{ stores.length }}</template
                ></span
              >
            </div>

            <!-- Barre de filtres -->
            <div v-if="stores.length" class="cdp__stores-toolbar">
              <!-- Recherche -->
              <div class="cdp__stores-search-wrap">
                <i class="pi pi-search cdp__stores-search-icon" />
                <input
                  v-model="storeSearch"
                  class="cdp__stores-search"
                  type="text"
                  :placeholder="t('clientDetail.storeSearch')"
                />
                <button
                  v-if="storeSearch"
                  class="cdp__stores-search-clear"
                  @click="storeSearch = ''"
                >
                  <i class="pi pi-times" />
                </button>
              </div>
              <!-- Filtre statut -->
              <div class="cdp__stores-status-btns">
                <button
                  v-for="opt in STORE_STATUS_OPTS"
                  :key="opt.k"
                  class="cdp__stores-status-btn"
                  :class="{
                    'cdp__stores-status-btn--active': storeStatus === opt.k,
                  }"
                  @click="storeStatus = opt.k"
                >
                  {{ opt.l }}
                </button>
              </div>
              <!-- Tri -->
              <div class="cdp__stores-sorts">
                <button
                  class="cdp__stores-sort-btn"
                  :class="{
                    'cdp__stores-sort-btn--active': storeSort === 'name',
                  }"
                  @click="toggleStoreSort('name')"
                >
                  {{ t('clientDetail.storeSort.name') }}
                  <i
                    class="pi"
                    :class="
                      storeSort === 'name'
                        ? storeSortDir === 'asc'
                          ? 'pi-sort-up-fill'
                          : 'pi-sort-down-fill'
                        : 'pi-sort'
                    "
                  />
                </button>
                <button
                  class="cdp__stores-sort-btn"
                  :class="{
                    'cdp__stores-sort-btn--active': storeSort === 'status',
                  }"
                  @click="toggleStoreSort('status')"
                >
                  {{ t('clientDetail.storeSort.status') }}
                  <i
                    class="pi"
                    :class="
                      storeSort === 'status'
                        ? storeSortDir === 'asc'
                          ? 'pi-sort-up-fill'
                          : 'pi-sort-down-fill'
                        : 'pi-sort'
                    "
                  />
                </button>
              </div>
            </div>

            <div v-if="!stores.length" class="cdp__empty-inline">
              <i class="pi pi-info-circle" />
              {{ t('clientDetail.empty.stores') }}
            </div>
            <div v-else-if="!filteredStores.length" class="cdp__empty-inline">
              <i class="pi pi-filter" /> {{ t('clientDetail.empty.noResult') }}
              <button
                class="cdp__btn cdp__btn--ghost cdp__btn--xs"
                @click="
                  storeSearch = '';
                  storeStatus = 'all';
                "
              >
                {{ t('clientDetail.reset') }}
              </button>
            </div>
            <div v-else class="cdp__stores">
              <div
                v-for="store in filteredStores"
                :key="store.id"
                class="cdp__store"
                @click="router.push(`/commerces/${store.id}`)"
              >
                <div class="cdp__store-icon">
                  <i class="pi pi-shop" />
                </div>
                <div class="cdp__store-body">
                  <div class="cdp__store-head">
                    <span class="cdp__store-name">{{ store.designation }}</span>
                    <span
                      class="cdp__status cdp__status--sm"
                      :class="
                        store.isActive
                          ? 'cdp__status--active'
                          : 'cdp__status--inactive'
                      "
                    >
                      <span class="cdp__status-dot" />{{
                        store.isActive
                          ? t('clientDetail.storeStatus.active')
                          : t('clientDetail.storeStatus.inactive')
                      }}
                    </span>
                  </div>
                  <div class="cdp__store-meta">
                    <span v-if="store.activityId" class="cdp__store-activity">{{
                      actLabel(store.activityId)
                    }}</span>
                    <span v-if="store.slug" class="cdp__store-slug">{{
                      store.slug
                    }}</span>
                  </div>
                  <div
                    v-if="store.schedules?.length"
                    class="cdp__store-schedules"
                  >
                    <span
                      v-for="s in store.schedules"
                      :key="s.weekday"
                      class="cdp__store-schedule"
                    >
                      {{ WEEKDAY[s.weekday] ?? s.weekday }}
                      {{ fmtHour(s.openTime) }}–{{ fmtHour(s.closeTime) }}
                    </span>
                  </div>
                </div>
                <button
                  class="cdp__store-arrow"
                  :title="t('clientDetail.storeArrow')"
                  @click.stop="router.push(`/commerces/${store.id}`)"
                >
                  <i class="pi pi-arrow-right" />
                </button>
              </div>
            </div>
          </section>

          <!-- Collections (masqué temporairement) -->
          <section v-if="false" class="cdp__card">
            <div class="cdp__card-header">
              <span class="cdp__card-title"
                ><i class="pi pi-folder" />
                {{ t('clientDetail.section.collections') }}</span
              >
              <span class="cdp__card-count">{{ orgCollectionsTotal }}</span>
            </div>

            <div v-if="!orgCollectionsTotal" class="cdp__collections-empty">
              <i class="pi pi-folder-open" />
              <span>{{ t('clientDetail.collectionsEmpty') }}</span>
            </div>

            <template v-else>
              <div class="cdp__collections-list">
                <div
                  v-for="cat in orgCollections"
                  :key="cat.id"
                  class="cdp__collections-row"
                >
                  <div class="cdp__collections-ico">
                    <i class="pi pi-tag" />
                  </div>
                  <span class="cdp__collections-name">{{
                    collectionDesignation(cat)
                  }}</span>
                  <span v-if="cat.position != null" class="cdp__collections-pos"
                    >#{{ cat.position }}</span
                  >
                </div>
              </div>

              <div class="cdp__collections-pagination">
                <div class="cdp__collections-pg-left">
                  <span class="cdp__collections-pg-info">
                    {{ (colPage - 1) * colPageSize + 1 }}–{{
                      Math.min(colPage * colPageSize, orgCollectionsTotal)
                    }}
                    {{ t('allCollections.paginationOf') }}
                    {{ orgCollectionsTotal }}
                  </span>
                  <select
                    v-model.number="colPageSize"
                    class="cdp__collections-pg-size"
                  >
                    <option v-for="sz in COL_PAGE_SIZES" :key="sz" :value="sz">
                      {{ sz }} {{ t('allCollections.perPage') }}
                    </option>
                  </select>
                </div>
                <div
                  v-if="orgCollectionsTotalPages > 1"
                  class="cdp__collections-pg-btns"
                >
                  <button
                    class="cdp__collections-pg-btn"
                    :disabled="colPage === 1"
                    @click="setColPage(1)"
                  >
                    <i class="pi pi-angle-double-left" />
                  </button>
                  <button
                    class="cdp__collections-pg-btn"
                    :disabled="colPage === 1"
                    @click="setColPage(colPage - 1)"
                  >
                    <i class="pi pi-angle-left" />
                  </button>
                  <span class="cdp__collections-pg-current"
                    >{{ colPage }} / {{ orgCollectionsTotalPages }}</span
                  >
                  <button
                    class="cdp__collections-pg-btn"
                    :disabled="colPage === orgCollectionsTotalPages"
                    @click="setColPage(colPage + 1)"
                  >
                    <i class="pi pi-angle-right" />
                  </button>
                  <button
                    class="cdp__collections-pg-btn"
                    :disabled="colPage === orgCollectionsTotalPages"
                    @click="setColPage(orgCollectionsTotalPages)"
                  >
                    <i class="pi pi-angle-double-right" />
                  </button>
                </div>
              </div>
            </template>
          </section>

          <!-- Timeline -->
          <section class="cdp__card">
            <div class="cdp__card-header">
              <span class="cdp__card-title"
                ><i class="pi pi-clock" />
                {{ t('clientDetail.section.activity') }}</span
              >
            </div>
            <div class="cdp__timeline">
              <div class="cdp__timeline-item">
                <div class="cdp__timeline-dot cdp__timeline-dot--ok" />
                <div class="cdp__timeline-body">
                  <p class="cdp__timeline-text">
                    {{ t('clientDetail.timeline.created') }}
                  </p>
                  <p class="cdp__timeline-date">{{ fmtDate(org.createdAt) }}</p>
                </div>
              </div>
              <div class="cdp__timeline-item">
                <div class="cdp__timeline-dot" />
                <div class="cdp__timeline-body">
                  <p class="cdp__timeline-text">
                    {{ t('clientDetail.timeline.updated') }}
                  </p>
                  <p class="cdp__timeline-date">{{ fmtDate(org.updatedAt) }}</p>
                </div>
              </div>
              <div v-if="stores.length" class="cdp__timeline-item">
                <div class="cdp__timeline-dot cdp__timeline-dot--ok" />
                <div class="cdp__timeline-body">
                  <p class="cdp__timeline-text">
                    {{ t('clientDetail.timeline.storeCount', stores.length) }}
                  </p>
                  <p class="cdp__timeline-date">
                    {{
                      t(
                        'clientDetail.timeline.activeCount',
                        activeStores.length,
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- ── Modal : Modifier ──────────────────────────────────────────────── -->
      <Teleport to="body">
        <div
          v-if="editOpen"
          class="cdp__modal-backdrop"
          @click.self="editOpen = false"
        >
          <div class="cdp__modal">
            <div class="cdp__modal-header">
              <span class="cdp__modal-title"
                ><i class="pi pi-pencil" />
                {{ t('clientDetail.editModal.title') }}</span
              >
              <button class="cdp__modal-close" @click="editOpen = false">
                <i class="pi pi-times" />
              </button>
            </div>
            <div class="cdp__modal-body">
              <div class="cdp__mfield">
                <label class="cdp__mlabel">{{
                  t('clientDetail.editModal.name')
                }}</label>
                <input
                  v-model="editForm.name"
                  class="cdp__minput"
                  type="text"
                  placeholder="Acme Belgium SA"
                />
              </div>
              <div class="cdp__mfield">
                <label class="cdp__mlabel">{{
                  t('clientDetail.editModal.orgType')
                }}</label>
                <select v-model="editForm.type" class="cdp__mselect">
                  <option
                    v-for="opt in ORG_TYPES_EDIT"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="cdp__mfield">
                <label class="cdp__mlabel">{{
                  t('clientDetail.editModal.tax')
                }}</label>
                <input
                  v-model="editForm.taxIdentificationNumber"
                  class="cdp__minput"
                  type="text"
                  placeholder="BE0123456789"
                />
              </div>
              <div class="cdp__mfield">
                <label class="cdp__mlabel">{{
                  t('clientDetail.editModal.country')
                }}</label>
                <input
                  v-model="editForm.country"
                  class="cdp__minput"
                  type="text"
                  placeholder="BE"
                  maxlength="2"
                />
              </div>
              <div class="cdp__mfield">
                <label class="cdp__mlabel">{{
                  t('clientDetail.editModal.description')
                }}</label>
                <input
                  v-model="editForm.description"
                  class="cdp__minput"
                  type="text"
                  :placeholder="t('clientDetail.editModal.descPlaceholder')"
                />
              </div>
              <p v-if="editError" class="cdp__merror">
                <i class="pi pi-exclamation-circle" /> {{ editError }}
              </p>
            </div>
            <div class="cdp__modal-footer">
              <button
                class="cdp__btn cdp__btn--ghost"
                @click="editOpen = false"
              >
                {{ t('clientDetail.editModal.cancel') }}
              </button>
              <button
                class="cdp__btn cdp__btn--primary"
                :disabled="updateOrganisation.isPending.value"
                @click="submitEdit"
              >
                <span
                  v-if="updateOrganisation.isPending.value"
                  class="cdp__spinner"
                />
                <i v-else class="pi pi-check" />
                {{
                  updateOrganisation.isPending.value
                    ? t('clientDetail.editModal.saving')
                    : t('clientDetail.editModal.save')
                }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- ── Modal : Désactiver ─────────────────────────────────────────────── -->
      <Teleport to="body">
        <div
          v-if="deactivateConfirm"
          class="cdp__modal-backdrop"
          @click.self="deactivateConfirm = false"
        >
          <div class="cdp__modal cdp__modal--sm">
            <div class="cdp__modal-header">
              <span class="cdp__modal-title"
                ><i class="pi pi-ban" />
                {{ t('clientDetail.deactivateModal.title') }}</span
              >
              <button
                class="cdp__modal-close"
                @click="deactivateConfirm = false"
              >
                <i class="pi pi-times" />
              </button>
            </div>
            <div class="cdp__modal-body">
              <p class="cdp__mtext">
                {{ t('clientDetail.deactivateModal.body', { name: org.name }) }}
              </p>
              <p class="cdp__mtext cdp__mtext--muted">
                {{ t('clientDetail.deactivateModal.info') }}
              </p>
              <p v-if="deactivateError" class="cdp__merror">
                <i class="pi pi-exclamation-circle" /> {{ deactivateError }}
              </p>
            </div>
            <div class="cdp__modal-footer">
              <button
                class="cdp__btn cdp__btn--ghost"
                @click="deactivateConfirm = false"
              >
                {{ t('clientDetail.deactivateModal.cancel') }}
              </button>
              <button
                class="cdp__btn cdp__btn--danger"
                :disabled="deactivateOrganisation.isPending.value"
                @click="submitDeactivate"
              >
                <span
                  v-if="deactivateOrganisation.isPending.value"
                  class="cdp__spinner cdp__spinner--white"
                />
                <i v-else class="pi pi-ban" />
                {{
                  deactivateOrganisation.isPending.value
                    ? t('clientDetail.deactivateModal.confirming')
                    : t('clientDetail.deactivateModal.confirm')
                }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style scoped>
/* ── Layout racine ────────────────────────────────────────────────────── */
/* nœud racine unique — reçoit les classes opacity de la transition App.vue */
.cdp__page {
  display: contents;
}
.cdp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .cdp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .cdp {
    padding: 1rem;
    gap: 1rem;
  }
}

/* ── Skeleton ─────────────────────────────────────────────────────────── */
.cdp__skeletons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cdp__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.cdp__sk--header {
  height: 6rem;
}
.cdp__sk__row {
  display: grid;
  grid-template-columns: 1fr 2.5fr;
  gap: 1.5rem;
}
.cdp__sk--card {
  height: 24rem;
}
.cdp__sk--main {
  height: 24rem;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Erreur ───────────────────────────────────────────────────────────── */
.cdp__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 5rem 1rem;
  text-align: center;
}
.cdp__error-icon {
  font-size: 3rem;
  color: var(--nxt-error);
  opacity: 0.5;
}

/* ── Header ───────────────────────────────────────────────────────────── */
.cdp__header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}

.cdp__breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.6;
}
.cdp__breadcrumb-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: inherit;
  transition: opacity 0.15s;
}
.cdp__breadcrumb-link:hover {
  opacity: 1;
  color: var(--nxt-primary);
}
.cdp__breadcrumb-sep {
  font-size: 0.625rem;
}
.cdp__breadcrumb-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
  opacity: 1;
}

.cdp__title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.cdp__title-left {
  display: flex;
  align-items: flex-start;
  gap: 1.125rem;
  flex: 1;
  min-width: 0;
}

.cdp__avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  overflow: hidden;
  flex-shrink: 0;
}
.cdp__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cdp__avatar--xl {
  width: 4rem;
  height: 4rem;
  border-radius: 1.125rem;
  font-size: 1.375rem;
}

.cdp__title-info {
  flex: 1;
  min-width: 0;
}
.cdp__title-name-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.375rem;
}
.cdp__title-name {
  margin: 0;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  color: var(--nxt-on-surface);
}
.cdp__title-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.65;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.cdp__title-sub .pi {
  font-size: 0.75rem;
}
.cdp__title-sep {
  opacity: 0.3;
}

.cdp__header-actions {
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}
@media (max-width: 480px) {
  .cdp__header-actions {
    flex-direction: column;
    width: 100%;
  }
  .cdp__header-actions .cdp__btn {
    width: 100%;
    justify-content: center;
  }
}

/* ── Status ────────────────────────────────────────────────────────────── */
.cdp__status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.225rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}
.cdp__status--sm {
  font-size: 0.6875rem;
  padding: 0.175rem 0.5rem;
}
.cdp__status--active {
  background: rgba(16, 181, 16, 0.08);
  color: #0a7a0a;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cdp__status--inactive {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__status-dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* ── Alert ─────────────────────────────────────────────────────────────── */
.cdp__alert {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: 0.875rem;
  font-size: 0.875rem;
}
.cdp__alert--warning {
  background: var(--nxt-surface-low, #f8f9fa);
  border: 1px solid var(--nxt-outline-variant);
  color: var(--nxt-on-surface);
}
.cdp__alert-icon {
  font-size: 1.125rem;
  color: var(--nxt-on-surface-variant);
  margin-top: 0.05rem;
}

/* ── Boutons ────────────────────────────────────────────────────────────── */
.cdp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.375rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    background 0.15s,
    filter 0.15s,
    transform 0.1s;
}
.cdp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.cdp__btn--primary:hover {
  background: #e6c109;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.4);
  transform: translateY(-1px);
}
.cdp__btn--ghost {
  background: #fff;
  color: rgba(11, 19, 32, 0.65);
  border: 1px solid rgba(11, 19, 32, 0.12);
}
.cdp__btn--ghost:hover {
  background: rgba(11, 19, 32, 0.04);
  color: #0b1320;
  border-color: rgba(11, 19, 32, 0.2);
}
.cdp__btn--sm {
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
}
.cdp__btn .pi {
  font-size: 0.875rem;
}

/* ── KPIs ──────────────────────────────────────────────────────────────── */
.cdp__kpis {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.875rem;
}
@media (min-width: 640px) {
  .cdp__kpis {
    grid-template-columns: repeat(4, 1fr);
  }
}

.cdp__kpi {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.125rem 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
}
.cdp__kpi:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.cdp__kpi-icon {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.625rem;
  background: var(--nxt-surface-low, #f5f5f5);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: var(--nxt-on-surface-variant);
}
.cdp__kpi-icon--muted {
  background: var(--nxt-surface-low);
  border-color: var(--nxt-outline-variant);
  color: var(--nxt-on-surface-muted);
}

.cdp__kpi-body {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  min-width: 0;
}
.cdp__kpi-label {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-on-surface-muted);
  opacity: 0.6;
}
.cdp__kpi-val {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
}
.cdp__kpi-val--sm {
  font-size: 1rem;
}
.cdp__kpi-sub {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.55;
}

/* ── Corps ──────────────────────────────────────────────────────────────── */
.cdp__body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: start;
}
@media (min-width: 1024px) {
  .cdp__body {
    grid-template-columns: minmax(0, 14rem) minmax(0, 1fr);
    gap: 1.5rem;
  }
}
@media (min-width: 1280px) {
  .cdp__body {
    grid-template-columns: minmax(0, 17rem) minmax(0, 1fr);
  }
}

.cdp__col {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── Cards ──────────────────────────────────────────────────────────────── */
.cdp__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.cdp__card--billing {
  border-color: rgba(255, 255, 255, 0.4);
}
.cdp__collections-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: var(--nxt-on-surface-muted, #938282);
  font-size: 0.8125rem;
}
.cdp__collections-empty .pi {
  font-size: 1.5rem;
  opacity: 0.35;
}
.cdp__collections-list {
  display: flex;
  flex-direction: column;
}
.cdp__collections-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.05));
}
.cdp__collections-row:last-child {
  border-bottom: none;
}
.cdp__collections-ico {
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  background: #111;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
}
[data-theme='dark'] .cdp__collections-ico {
  background: #fff;
  color: #111;
}
.cdp__collections-name {
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cdp__collections-pos {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted, #938282);
  flex-shrink: 0;
}
.cdp__collections-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.cdp__collections-pg-left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.cdp__collections-pg-info {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #938282);
}
.cdp__collections-pg-size {
  font-size: 0.6875rem;
  font-family: inherit;
  padding: 0.2rem 0.4rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.375rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  cursor: pointer;
  outline: none;
}
.cdp__collections-pg-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.cdp__collections-pg-btn {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.3125rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5625rem;
  color: var(--nxt-on-surface-muted, #938282);
}
.cdp__collections-pg-btn:hover:not(:disabled) {
  border-color: var(--nxt-primary, #f68402);
  color: var(--nxt-primary, #f68402);
}
.cdp__collections-pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.cdp__collections-pg-current {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  padding: 0 0.2rem;
  white-space: nowrap;
}

.cdp__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.375rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  background: transparent;
}
.cdp__card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__card-title .pi {
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
}
.cdp__card-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 800;
}

/* ── Fields ─────────────────────────────────────────────────────────────── */
.cdp__fields {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cdp__field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.cdp__field--full {
}
.cdp__field-lbl {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-on-surface-muted);
  opacity: 0.55;
}
.cdp__field-val {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cdp__field-val--mono {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.8125rem;
}

.cdp__type-chip {
  display: inline-block;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── List (adresses / contacts) ──────────────────────────────────────────── */
.cdp__list {
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cdp__list-item {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.5);
  transition: border-color 0.15s;
}
.cdp__list-item--primary {
  border-color: var(--nxt-outline-variant);
  background: var(--nxt-card-bg, #fff);
}

.cdp__list-item-icon {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.cdp__list-item--primary .cdp__list-item-icon {
  color: var(--nxt-on-surface-variant);
  border-color: var(--nxt-outline-variant);
}

.cdp__list-item-body {
  flex: 1;
  min-width: 0;
}
.cdp__list-item-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.2rem;
}
.cdp__list-item-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__list-item-detail {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.7;
}

.cdp__badge-primary {
  font-size: 0.5625rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.175rem 0.4rem;
  border-radius: 0.25rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}

.cdp__contact-avatar {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 800;
}
.cdp__list-item-pos {
  margin: 0 0 0.375rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.6;
}
.cdp__contact-links {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.cdp__contact-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  text-decoration: none;
  transition: opacity 0.15s;
}
.cdp__contact-link:hover {
  opacity: 0.75;
}
.cdp__contact-link .pi {
  font-size: 0.75rem;
}

.cdp__empty-inline {
  padding: 1.5rem;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.45;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}

/* ── Stores ──────────────────────────────────────────────────────────────── */
.cdp__stores {
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cdp__store {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.cdp__store:hover {
  border-color: rgba(0, 0, 0, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.cdp__store-icon {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: var(--nxt-on-surface-variant);
}
.cdp__store-body {
  flex: 1;
  min-width: 0;
}
.cdp__store-head {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
  margin-bottom: 0.15rem;
}
.cdp__store-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__store-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}
.cdp__store-activity {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 0.3rem;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.cdp__store-slug {
  font-size: 0.75rem;
  font-family: 'Menlo', 'Consolas', monospace;
  color: var(--nxt-on-surface-muted);
  opacity: 0.5;
}
.cdp__store-schedules {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.375rem;
}
.cdp__store-schedule {
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__store-arrow {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-primary);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.cdp__store-arrow:hover {
  background: rgba(0, 0, 0, 0.08);
}

/* ── Stores toolbar ─────────────────────────────────────────────────────── */
.cdp__stores-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  background: rgba(0, 0, 0, 0.02);
}
.cdp__stores-search-wrap {
  position: relative;
  flex: 1;
  min-width: 10rem;
}
.cdp__stores-search-icon {
  position: absolute;
  left: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.5;
  pointer-events: none;
}
.cdp__stores-search {
  width: 100%;
  height: 2rem;
  padding: 0 2rem 0 2rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
}
.cdp__stores-search:focus {
  border-color: var(--nxt-primary);
}
.cdp__stores-search-clear {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.5;
}
.cdp__stores-status-btns {
  display: flex;
  gap: 0.25rem;
}
.cdp__stores-status-btn {
  height: 2rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.cdp__stores-status-btn--active {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
.cdp__stores-sorts {
  display: flex;
  gap: 0.25rem;
}
.cdp__stores-sort-btn {
  height: 2rem;
  padding: 0 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.15s;
}
.cdp__stores-sort-btn--active {
  color: var(--nxt-primary);
  border-color: var(--nxt-primary);
}
.cdp__stores-sort-btn .pi {
  font-size: 0.625rem;
}
.cdp__btn--xs {
  height: 1.75rem;
  padding: 0 0.625rem;
  font-size: 0.6875rem;
}

/* ── Billing ────────────────────────────────────────────────────────────── */
.cdp__billing-notice {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  margin: 0;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 0 0 1rem 1rem;
}
.cdp__billing-notice-icon {
  font-size: 1.375rem;
  color: var(--nxt-primary);
  margin-top: 0.1rem;
  flex-shrink: 0;
}
.cdp__billing-notice-body {
  flex: 1;
  min-width: 0;
}
.cdp__billing-notice-body strong {
  display: block;
  font-size: 0.875rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  margin-bottom: 0.5rem;
}
.cdp__billing-notice-body p {
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--nxt-on-surface-muted);
  opacity: 0.75;
}
.cdp__billing-chips {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.cdp__billing-chip {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__billing-chip-lbl {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-muted);
  opacity: 0.55;
}
.cdp__billing-chip-val {
  font-size: 0.875rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cdp__billing-cta {
}

/* ── Timeline ────────────────────────────────────────────────────────────── */
.cdp__timeline {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.cdp__timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding-bottom: 1rem;
  position: relative;
}
.cdp__timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 0.4375rem;
  top: 1rem;
  width: 1px;
  bottom: 0;
  background: var(--nxt-outline-variant);
}
.cdp__timeline-dot {
  width: 0.875rem;
  height: 0.875rem;
  min-width: 0.875rem;
  border-radius: 50%;
  background: var(--nxt-surface-mid);
  border: 2px solid var(--nxt-outline-variant);
  margin-top: 0.2rem;
  position: relative;
  z-index: 1;
}
.cdp__timeline-dot--ok {
  background: rgba(16, 181, 16, 0.15);
  border-color: rgba(16, 181, 16, 0.4);
}
.cdp__timeline-body {
}
.cdp__timeline-text {
  margin: 0 0 0.1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cdp__timeline-date {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.5;
}

/* ── Bouton danger ───────────────────────────────────────────────────────── */
.cdp__btn--danger {
  background: var(--nxt-error, #ba1a1a);
  color: #fff;
  border: none;
}
.cdp__btn--danger:not(:disabled):hover {
  filter: brightness(1.1);
}
.cdp__btn--success {
  background: var(--nxt-status-up, #10b510);
  color: #fff;
  border: none;
}
.cdp__btn--success:not(:disabled):hover {
  filter: brightness(1.1);
}

/* ── Modals ──────────────────────────────────────────────────────────────── */
.cdp__modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cdp__modal {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cdp__modal--sm {
  max-width: 380px;
}
.cdp__modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cdp__modal-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cdp__modal-close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all 0.15s;
}
.cdp__modal-close:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cdp__modal-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cdp__modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.cdp__mfield {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cdp__mlabel {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cdp__minput {
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
}
.cdp__minput:focus {
  border-color: var(--nxt-primary);
}
.cdp__mselect {
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  appearance: none;
  cursor: pointer;
}
.cdp__mselect:focus {
  border-color: var(--nxt-primary);
}
.cdp__mtext {
  margin: 0;
  font-size: 0.9rem;
  color: var(--nxt-on-surface);
  line-height: 1.5;
}
.cdp__mtext--muted {
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}
.cdp__merror {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: var(--nxt-error-container);
  color: var(--nxt-on-error-container);
  font-size: 0.875rem;
  margin: 0;
}
.cdp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid var(--nxt-outline-variant);
  border-top-color: var(--nxt-primary);
  animation: cdp-spin 0.7s linear infinite;
}
.cdp__spinner--white {
  border-color: rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
}
@keyframes cdp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
[data-theme='dark'] .cdp {
  background: #0c1421;
}
[data-theme='dark'] .cdp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.2);
}
[data-theme='dark'] .cdp__btn--primary:hover {
  background: #ffe033;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .cdp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cdp__header {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__kpi {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__kpi:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .cdp__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__card-header {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__list-item {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__list-item--primary {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__store {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__store:hover {
  border-color: rgba(255, 214, 10, 0.35);
  box-shadow: none;
}
[data-theme='dark'] .cdp__stores-toolbar {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__stores-search {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cdp__stores-search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .cdp__stores-status-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cdp__stores-status-btn--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cdp__stores-sort-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cdp__stores-sort-btn--active {
  color: #ffd60a;
  border-color: #ffd60a;
}
[data-theme='dark'] .cdp__billing-notice {
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .cdp__billing-chip {
  background: var(--nxt-surface-high, #2a2d30);
  border-color: rgba(255, 255, 255, 0.08);
}
</style>
